"use client";

/**
 * RichPromptInput Component
 *
 * A rich text input that displays @mentions with highlighted backgrounds.
 * Uses an overlay technique to show colored backgrounds behind mention text.
 */

import React, { useRef, useEffect, useCallback } from "react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { IdentitySearchResult } from "@/stores/MentionStore";

interface RichPromptInputProps {
  /** Current text value */
  value: string;
  /** Change handler */
  onValueChange: (value: string) => void;
  /** Submit handler (Enter key) */
  onSubmit?: (event: React.FormEvent) => void;
  /** Paste handler */
  onPaste?: (event: React.ClipboardEvent) => void;
  /**
   * Whether an overlay (mention autocomplete or "/" command menu) is open.
   * When true, the Enter key is swallowed instead of submitting so the open
   * overlay can handle it. RichPromptInput does not distinguish which overlay
   * is open; the parent computes the OR of its separate menu states.
   */
  isOverlayOpen?: boolean;
  /** Map of selected mentions for styling */
  selectedMentions?: Map<string, IdentitySearchResult>;
  /** Messages count for placeholder */
  messagesCount?: number;
  /** Overrides the default messages-count-derived placeholder when provided */
  placeholder?: string;
  /** Minimum rows */
  minRows?: number;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Additional class names */
  classNames?: {
    inputWrapper?: string;
    innerWrapper?: string;
    input?: string;
  };
  /** End content (send button) */
  endContent?: React.ReactNode;
  /** Data attributes */
  "data-id"?: string;
  /** Whether to expand the textarea to fill available height */
  expandToFill?: boolean;
}

/**
 * Escape special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Parse text and identify mention positions for styling.
 * Builds patterns from known mention names so multi-word names are matched fully.
 */
function parseMentionPositions(
  text: string,
  selectedMentions: Map<string, IdentitySearchResult>,
): Array<{ start: number; end: number; displayName: string; identity: IdentitySearchResult }> {
  const positions: Array<{
    start: number;
    end: number;
    displayName: string;
    identity: IdentitySearchResult;
  }> = [];

  // Build regex from known display names (longest first to avoid partial matches)
  const entries = Array.from(selectedMentions.entries()).sort(
    (a, b) => b[1].displayName.length - a[1].displayName.length,
  );

  for (const [, identity] of entries) {
    const pattern = new RegExp(`@${escapeRegex(identity.displayName)}(?=\\s|$|@)`, "gi");
    let match;

    while ((match = pattern.exec(text)) !== null) {
      // Skip if this position overlaps with an already-found mention
      const overlaps = positions.some(
        (p) => match!.index < p.end && match!.index + match![0].length > p.start,
      );

      if (!overlaps) {
        positions.push({
          start: match.index,
          end: match.index + match[0].length,
          displayName: identity.displayName,
          identity,
        });
      }
    }
  }

  // Sort by position in text
  positions.sort((a, b) => a.start - b.start);

  return positions;
}

/**
 * Render text with background highlights for mentions.
 * Non-mention text is invisible (textarea shows it).
 * Mention text has a colored background.
 */
function renderBackgroundHighlights(
  text: string,
  selectedMentions: Map<string, IdentitySearchResult>,
): React.ReactNode[] {
  const positions = parseMentionPositions(text, selectedMentions);

  if (positions.length === 0) {
    return [
      <span key="text" className="invisible" style={{ whiteSpace: "pre-wrap" }}>
        {text || "\u00A0"}
      </span>,
    ];
  }

  const elements: React.ReactNode[] = [];
  let lastIndex = 0;

  positions.forEach((pos, idx) => {
    // Add invisible text before mention (preserves spacing)
    if (pos.start > lastIndex) {
      elements.push(
        <span key={`text-${idx}`} className="invisible" style={{ whiteSpace: "pre-wrap" }}>
          {text.slice(lastIndex, pos.start)}
        </span>,
      );
    }

    // Add mention with colored background (text invisible, bg visible)
    elements.push(
      <span
        key={`mention-${idx}`}
        className="py-0.5 rounded bg-primary/30"
        style={{ whiteSpace: "pre-wrap" }}
      >
        <span className="invisible">{text.slice(pos.start, pos.end)}</span>
      </span>,
    );

    lastIndex = pos.end;
  });

  // Add invisible text after last mention
  if (lastIndex < text.length) {
    elements.push(
      <span key="text-end" className="invisible" style={{ whiteSpace: "pre-wrap" }}>
        {text.slice(lastIndex)}
      </span>,
    );
  }

  return elements;
}

export const RichPromptInput = React.forwardRef<HTMLTextAreaElement, RichPromptInputProps>(
  (
    {
      value,
      onValueChange,
      onSubmit,
      onPaste,
      isOverlayOpen = false,
      selectedMentions = new Map(),
      messagesCount = 0,
      placeholder: placeholderOverride,
      minRows = 3,
      disabled = false,
      classNames = {},
      endContent,
      "data-id": dataId,
      expandToFill = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Combine refs
    const setRefs = useCallback(
      (element: HTMLTextAreaElement | null) => {
        textareaRef.current = element;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    const placeholder =
      placeholderOverride ?? (messagesCount === 0 ? t("Start new chat") : t("Type a message"));

    // Check if we have any styled mentions to show
    const hasMentions = selectedMentions.size > 0;

    // Sync scroll between textarea and overlay
    useEffect(() => {
      const textarea = textareaRef.current;
      const overlay = overlayRef.current;

      if (!textarea || !overlay) return;

      const syncScroll = () => {
        overlay.scrollTop = textarea.scrollTop;
        overlay.scrollLeft = textarea.scrollLeft;
      };

      // Initial sync
      syncScroll();

      textarea.addEventListener("scroll", syncScroll);

      return () => textarea.removeEventListener("scroll", syncScroll);
    }, [hasMentions, value]);

    // Handle keyboard events
    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        // Don't submit or insert newline if an overlay (mention autocomplete
        // or command menu) is open; let the open overlay handle the Enter key.
        if (isOverlayOpen) {
          event.preventDefault();

          return;
        }
        event.preventDefault();
        onSubmit?.(event);
      }
    };

    // Handle text change
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onValueChange(event.target.value);
    };

    return (
      <div
        ref={containerRef}
        className={cn(
          "relative w-full px-3 py-2",
          classNames.inputWrapper,
          expandToFill && "flex-1 min-h-0 flex flex-col",
        )}
      >
        <div
          className={cn(
            "relative",
            classNames.innerWrapper,
            expandToFill && "flex-1 min-h-0 flex flex-col",
          )}
        >
          {/* Background overlay - shows colored backgrounds for mentions */}
          {hasMentions && (
            <div
              ref={overlayRef}
              aria-hidden="true"
              className={cn(
                "absolute inset-0 pointer-events-none overflow-hidden",
                "pt-1 pl-2 pb-6 pr-14 text-medium",
              )}
              style={{
                lineHeight: "1.5",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              {renderBackgroundHighlights(value, selectedMentions)}
            </div>
          )}

          {/* Actual textarea - always shows text */}
          <textarea
            ref={setRefs}
            data-hj-suppress
            aria-label={t("Prompt")}
            className={cn(
              "w-full resize-none bg-transparent relative",
              !expandToFill && "min-h-25",
              expandToFill && "flex-1",
              "pt-1 pl-2 pb-6 pr-14 text-medium text-foreground",
              "focus:outline-none border-none",
              "[&::selection]:bg-brand-secondary [&::-moz-selection]:bg-brand-secondary",
              classNames.input,
            )}
            data-id={dataId}
            disabled={disabled}
            placeholder={placeholder}
            rows={minRows}
            style={{ lineHeight: "1.5", scrollbarGutter: "stable", scrollbarWidth: "thin" }}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={onPaste}
          />

          {/* End content (send button) - inset clear of scrollbar, with a soft backdrop */}
          {endContent && (
            <div className="absolute top-1 right-3 z-20 rounded-xl bg-content1/60 dark:bg-content1/40 ring-1 ring-default-200/40 dark:ring-default-100/10">
              {endContent}
            </div>
          )}
        </div>
      </div>
    );
  },
);

RichPromptInput.displayName = "RichPromptInput";

export default RichPromptInput;
